/**
 * @file React view of the offline pick queue, with automatic retry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PICK_QUEUE_EVENT,
  PICK_QUEUE_STORAGE_KEY,
  flushPickQueue,
  getPickQueueStatus,
} from '../utils/pickQueue';
import { useOnlineStatus } from './useOnlineStatus';

/** Retry cadence while writes are still waiting to reach the server. */
const RETRY_INTERVAL_MS = 15_000;

/**
 * @param {{onSynced?: (entry: object, result: object) => void,
 *   onRejected?: (entry: object, error: Error) => void}} [handlers] `onSynced`
 *   reconciles a confirmed write; `onRejected` undoes one the server refused.
 */
export function usePickQueue(handlers = {}) {
  const isOnline = useOnlineStatus();
  const [status, setStatus] = useState(getPickQueueStatus);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const refresh = () => setStatus(getPickQueueStatus());
    const onStorage = (e) => {
      if (e.key === null || e.key === PICK_QUEUE_STORAGE_KEY) refresh();
    };
    window.addEventListener(PICK_QUEUE_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(PICK_QUEUE_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const flush = useCallback(
    () => flushPickQueue({
      onSynced: (entry, result) => handlersRef.current.onSynced?.(entry, result),
      onRejected: (entry, error) => handlersRef.current.onRejected?.(entry, error),
    }),
    []
  );

  // Retry whenever the connection or the tab comes back, and on a slow timer
  // while anything is still waiting.
  useEffect(() => {
    if (!isOnline) return undefined;

    flush();

    const onVisible = () => {
      if (document.visibilityState === 'visible') flush();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => {
      if (getPickQueueStatus().pendingCount > 0) flush();
    }, RETRY_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [isOnline, flush]);

  return {
    isOnline,
    pending: status.pending,
    pendingCount: status.pendingCount,
    syncing: status.syncing,
    lastError: status.lastError,
    flush,
  };
}

export default usePickQueue;
